// Drives the favicon status dot from manual-analysis activity: amber while any
// panel runs, then a green (no errors) / red (any error) dot when the batch
// finishes. The result dot persists until the tab is focused again, then clears
// 5s later — so a result you missed while away is still visible when you return.
// Call once at the app root.
import { useEffect, useRef } from 'react';
import { useTabActivityStore, selectBusyCount } from 'store/useTabActivityStore';
import { setFaviconBadge, clearFaviconBadge } from 'utils/favicon';

const RESULT_CLEAR_MS = 5000;

export function useFaviconBadge(): void {
  const busyCount = useTabActivityStore(selectBusyCount);
  const batchHadError = useTabActivityStore((s) => s.batchHadError);
  const batchHadSuccess = useTabActivityStore((s) => s.batchHadSuccess);

  const prevBusyRef = useRef(0);
  const clearTimerRef = useRef<number | null>(null);
  // Removes the visibilitychange listener if one is pending; null when none.
  const visCleanupRef = useRef<(() => void) | null>(null);

  // Latest outcome flags, read only at the moment the batch ends (avoids
  // retriggering the effect mid-batch as flags flip).
  const errorRef = useRef(batchHadError);
  const successRef = useRef(batchHadSuccess);
  errorRef.current = batchHadError;
  successRef.current = batchHadSuccess;

  useEffect(() => {
    // Cancel any pending result clear (timer + focus listener).
    const teardownPending = () => {
      if (clearTimerRef.current !== null) {
        window.clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
      if (visCleanupRef.current) {
        visCleanupRef.current();
        visCleanupRef.current = null;
      }
    };

    const prev = prevBusyRef.current;
    prevBusyRef.current = busyCount;

    if (busyCount > 0) {
      // Active run: amber dot, drop any pending result clear.
      teardownPending();
      setFaviconBadge('amber');
      return;
    }

    // busyCount === 0 — only act on the >0 → 0 transition (batch just finished).
    if (prev === 0) return;

    const color = errorRef.current ? 'red' : successRef.current ? 'green' : null;
    if (!color) {
      clearFaviconBadge(); // abort-only batch: nothing to show
      return;
    }

    setFaviconBadge(color);

    // Once the tab is focused, leave the dot up for 5s, then clear it.
    const scheduleClear = () => {
      clearTimerRef.current = window.setTimeout(() => {
        clearTimerRef.current = null;
        clearFaviconBadge();
      }, RESULT_CLEAR_MS);
    };

    if (document.visibilityState === 'visible') {
      scheduleClear();
    } else {
      const onVisible = () => {
        if (document.visibilityState !== 'visible') return;
        visCleanupRef.current?.();
        visCleanupRef.current = null;
        scheduleClear();
      };
      document.addEventListener('visibilitychange', onVisible);
      visCleanupRef.current = () => document.removeEventListener('visibilitychange', onVisible);
    }
  }, [busyCount]);

  // Restore the resting favicon + drop pending handlers on unmount.
  useEffect(
    () => () => {
      if (clearTimerRef.current !== null) window.clearTimeout(clearTimerRef.current);
      visCleanupRef.current?.();
      clearFaviconBadge();
    },
    [],
  );
}
